import React from "react";
import { getCompanyConfig } from "@/config/company";
import { formatNumber, formatDate } from "@/lib/utils";
import ISOPrintHeader from "@/components/shared/print/ISOPrintHeader";
import { PrintDocumentTitle, PrintInfoGrid } from "@/components/shared/print";

interface WipTransferPrintTemplateProps {
  transfer?: any;
  data?: any;
  config?: any;
}

const fmt = (n: number | undefined) => formatNumber(n || 0);

export default function WipTransferPrintTemplate({ transfer, data, config }: WipTransferPrintTemplateProps) {
  const d = transfer || data;
  if (!d) return null;

  const c = getCompanyConfig(config);
  const date = d.transferDate || d.createdAt ? new Date(d.transferDate || d.createdAt) : new Date();

  const po = d.productionOrder || {};
  const orderCode = po.orderId || d.lsxId || d.productionOrderId || "—";
  const productName = po.productName || d.productName || "Bao bì carton Offset";
  const fromStepName = d.fromRoutingStep?.name || d.fromStep || d.fromStage || "—";
  const toStepName = d.toRoutingStep?.name || d.toStep || d.toStage || "—";
  const qty = d.quantity || d.qtyTransferred || 0;
  const receivedQty = d.receivedQty;
  const rejectedQty = d.rejectedQty || 0;
  const docId = d.transferId || d.code || (d.id ? `WIP-${d.id.slice(0, 8)}` : "");

  return (
    <div
      className="kh-print-doc p-[10mm] text-black bg-white w-full mx-auto min-h-[297mm] text-[12px]"
      style={{ fontFamily: "'Times New Roman', Times, serif" }}
      id="print-area"
    >
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 10mm; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          #print-area { max-width: none !important; margin: 0 !important; }
        }
        .wip-table th, .wip-table td { border: 1px solid #000; padding: 8px; vertical-align: middle; }
        .wip-table th { background-color: #f1f5f9 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .wip-table tr { break-inside: avoid; page-break-inside: avoid; }
        .print-avoid-break { break-inside: avoid; page-break-inside: avoid; }
      `}</style>

      {/* 1. HEADER CÔNG TY */}
      <ISOPrintHeader 
        config={config} 
      />

      {/* 2. DOCUMENT TITLE (CENTERED, NO QR) */}
      <PrintDocumentTitle
        title="THẺ BÀN GIAO BTP"
        subtitle="WIP STAGE TRANSFER TICKET"
        docNo={docId}
        date={date}
        dateLabel="Ngày giao"
        extraMeta={
          <span>
            Lệnh SX: <strong className="font-mono text-indigo-900">{orderCode}</strong>
            {d.palletCode && (
              <> • Kiện Pallet: <strong className="font-mono text-slate-800">{d.palletCode}</strong></>
            )}
          </span>
        }
      />

      {/* DETAILS (BORDERLESS 2-COL TABS) */}
      <div className="mb-5">
        <PrintInfoGrid
          labelWidth="w-[130px]"
          leftColumn={[
            {
              label: "Sản phẩm",
              value: productName,
              isBold: true,
            },
            ...(po.customer?.name ? [{
              label: "Khách hàng",
              value: po.customer.name,
            }] : []),
          ]}
          rightColumn={[
            {
              label: "Số phiếu",
              value: docId,
              isBold: true,
              highlight: true,
            },
            {
              label: "Quy cách / Ghi chú",
              value: d.notes || "Bán thành phẩm theo quy trình công đoạn",
            },
          ]}
        />
      </div>

      {/* TRANSFER INFO TABLE */}
      <table className="w-full border-collapse wip-table text-[12px] table-fixed mb-5">
        <thead>
          <tr className="bg-slate-100 font-bold text-center uppercase">
            <th style={{ width: "24%" }}>Công đoạn giao</th>
            <th style={{ width: "24%" }}>Công đoạn nhận</th>
            <th style={{ width: "18%" }}>SL Bàn giao</th>
            <th style={{ width: "18%" }}>SL Thực nhận</th>
            <th style={{ width: "16%" }}>Hao hụt / Lỗi</th>
          </tr>
        </thead>
        <tbody>
          <tr className="text-center">
            <td className="font-bold text-indigo-900">{fromStepName}</td>
            <td className="font-bold text-emerald-900">{toStepName}</td>
            <td className="font-bold font-mono text-[13px] text-slate-900">{fmt(qty)} Tờ</td>
            <td className="font-bold font-mono text-[13px] text-emerald-700">
              {receivedQty !== null && receivedQty !== undefined ? `${fmt(receivedQty)} Tờ` : "Chờ nhận"}
            </td>
            <td className="font-mono text-rose-600 font-semibold">
              {rejectedQty > 0 ? `${fmt(rejectedQty)} Tờ` : "0"}
            </td>
          </tr>
        </tbody>
      </table>

      {d.rejectionReason && (
        <div className="mb-4 p-2 bg-rose-50 border border-rose-200 text-rose-800 text-[11px] rounded">
          <span className="font-bold">Lý do lỗi / hao hụt:</span> {d.rejectionReason}
        </div>
      )}

      <div className="mb-5">
        <div className="border border-black p-3 text-[11px]">
          <span className="font-bold italic">Lưu ý kiểm tra ngoại quan (KCS / Thợ giao ca):</span>
          <div className="grid grid-cols-3 gap-2 mt-2">
            <div>☐ Đúng quy cách xớ giấy</div>
            <div>☐ Không lem màu, không nhem</div>
            <div>☐ Đúng số lượng kiện pallet</div>
          </div>
          <div className="mt-3 border-b border-dotted border-black w-full"></div>
        </div>
      </div>

      {/* DATE */}
      <div className="text-right text-[11px] italic mb-3">
        Ngày {date.getDate()} tháng {date.getMonth() + 1} năm {date.getFullYear()}
      </div>

      {/* SIGNATURES */}
      <div className="grid grid-cols-3 text-center text-[11px] print-avoid-break">
        <div className="space-y-16 border border-black p-3 border-r-0">
          <div className="font-bold uppercase">Thợ Trạm Giao</div>
          <div className="text-[10px] text-slate-700">
            {d.senderName || "(Ký & ghi rõ họ tên)"}
          </div>
        </div>
        <div className="space-y-16 border border-black p-3 border-r-0">
          <div className="font-bold uppercase">KCS / Vận Chuyển</div>
          <div className="text-[10px] italic text-slate-400">(Ký &amp; ghi rõ họ tên)</div>
        </div>
        <div className="space-y-16 border border-black p-3">
          <div className="font-bold uppercase">Thợ Trạm Nhận</div>
          <div className="text-[10px] text-slate-700">
            {d.receiverName || "(Ký &amp; ghi rõ họ tên)"}
          </div>
        </div>
      </div>

      {/* FOOTER NOTE */}
      <div className="mt-6 text-[10px] italic text-slate-500">
        * Lưu ý: Thẻ bàn giao này bắt buộc phải đi kèm kiện Pallet bán thành phẩm xuyên suốt các công đoạn xưởng in.
      </div>
    </div>
  );
}
